import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

/**
 * Calls `onVisible` the first time the referenced element enters the viewport,
 * then stops observing.
 *
 * Impressions are counted once per render of a surface, so the latch is the
 * point: an element scrolled away from and back to has not been seen twice.
 *
 * An undefined `onVisible` observes nothing at all, rather than observing and
 * discarding the result. That is what keeps the hook free for a consumer that
 * does not want impressions: no observer is constructed, so no environment
 * without `IntersectionObserver` has to grow one.
 *
 * `@noths/polaris-client-react-hooks` has `useImpressionRef`, which does the
 * same job for the web apps. This is deliberately a local copy rather than a new
 * dependency: the design system should not need a client-side package to observe
 * its own elements.
 */
export const useOnVisible = (ref: RefObject<Element>, onVisible: (() => void) | undefined) => {
  /* Held in a ref so that a consumer passing an inline arrow function, which
     almost all of them do, does not tear down and re-create the observer on
     every render. The trade is that a handler arriving after the first render is
     not picked up, since the effect has already decided there was none; every
     consumer so far has its handler from the start. */
  const callback = useRef(onVisible);
  callback.current = onVisible;

  useEffect(() => {
    const element = ref.current;

    if (!element || !callback.current) {
      return undefined;
    }

    /* Default options, so any pixel on screen counts. An impression is that the
       customer had the chance to see the product, not that they dwelt on it. */
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        observer.disconnect();
        callback.current?.();
      }
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [ref]);
};
